import React, { createContext, useContext, useEffect, useState } from "react";
import defaultContent from "./defaultContent.json";
import { normalizeContent } from "./normalizeContent";

// Single source of truth for everything editable on the public site.
//
// On mount, fetches `/api/content`. If KV has a *valid* document, it
// replaces the bundled snapshot. If KV is empty, the request fails, the
// network is offline, OR the document is malformed, we keep the bundled
// `defaultContent.json` and the page renders exactly as it does today.

function isStr(v) {
  return typeof v === "string";
}

// Sanity check — covers what the public components actually read. Anything
// less than this would crash the React tree on render. Mirrors (a subset
// of) the server-side schema validator.
function isValidDoc(d) {
  if (!d || typeof d !== "object") return false;
  if (!d.hero || !isStr(d.hero.image) || !isStr(d.hero.h1)) return false;
  if (!d.worksIntro || !isStr(d.worksIntro.video)) return false;
  if (
    !(typeof d.worksIntro.cta === "string" ||
      (d.worksIntro.cta && typeof d.worksIntro.cta === "object" && typeof d.worksIntro.cta.label === "string"))
  ) return false;
  if (!d.worksDeeper || !isStr(d.worksDeeper.background)) return false;
  if (
    !d.contact ||
    !isStr(d.contact.tagline) ||
    !isStr(d.contact.email) ||
    !isStr(d.contact.phone) ||
    !isStr(d.contact.image)
  ) {
    return false;
  }
  if (!Array.isArray(d.works)) return false;
  for (const w of d.works) {
    if (!w || typeof w !== "object") return false;
    if (!isStr(w.slug) || !isStr(w.title) || !isStr(w.image)) return false;
    if (!Array.isArray(w.gallery)) return false;
  }
  return true;
}

const ContentContext = createContext(defaultContent);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(() => normalizeContent(defaultContent));

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    fetch("/api/content", { signal: ctrl.signal, credentials: "same-origin" })
      .then(async (r) => {
        clearTimeout(t);
        if (!r.ok) return null;
        try {
          return await r.json();
        } catch {
          return null;
        }
      })
      .then((doc) => {
        if (alive && isValidDoc(doc)) setContent(normalizeContent(doc));
      })
      .catch(() => {
        // Network error / abort / malformed. Bundled default already in state.
      });
    return () => {
      alive = false;
      clearTimeout(t);
      ctrl.abort();
    };
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    if (sessionStorage.getItem("rf_view_sent")) return;
    sessionStorage.setItem("rf_view_sent", "1");
    fetch("/api/views", { method: "POST", credentials: "same-origin" }).catch(() => {});
  }, []);

  return (
    <ContentContext.Provider value={content}>{children}</ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

export function useWorks() {
  return useContent().works;
}
